import { z } from "zod";
import { conditionIdSchema } from "./condition.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { observationIdSchema } from "./observation.schemas";
import { patientIdSchema } from "./patient.schemas";
import { codeableConceptSchema } from "./schedule.schemas";

/**
 * Record a symptom (Observation) and optionally attach it as evidence to one
 * or more Conditions in a single intent. `attachedTo: []` leaves the Observation
 * unattributed (surfaced later via CarePlan.supportingInfo or promoted by a
 * clinician into Condition.evidence).
 */
export const recordSymptomInputSchema = z.object({
  id: newEntityIdSchema,
  patient: z.object({ id: patientIdSchema }),
  /** SNOMED symptom/finding code from the picker. */
  code: codeableConceptSchema,
  /** Drives Observation.category — survey (PRO) vs exam (clinician). */
  source: z.enum(["patient-reported", "clinician-observed"]),
  /** Point-in-time the symptom was observed. Defaults to now in the handler. */
  effectiveDateTime: z.string().datetime().optional(),
  /** Optional severity coding (e.g. SNOMED mild/moderate/severe). */
  severity: codeableConceptSchema.optional(),
  note: z.string().optional(),
  /** Conditions this symptom manifests. Empty = unattributed. */
  attachedTo: z.array(z.object({ conditionId: conditionIdSchema })).default([]),
});
export type RecordSymptomInput = z.infer<typeof recordSymptomInputSchema>;

/**
 * Correct a recorded symptom Observation. The code (identity) is immutable —
 * only source (category), severity, and note are editable.
 */
export const updateSymptomInputSchema = z.object({
  observationId: observationIdSchema,
  /** Re-derives Observation.category — survey (PRO) vs exam (clinician). */
  source: z.enum(["patient-reported", "clinician-observed"]).optional(),
  /** Severity coding (SNOMED mild/moderate/severe). */
  severity: codeableConceptSchema.optional(),
  note: z.string().optional(),
});
export type UpdateSymptomInput = z.infer<typeof updateSymptomInputSchema>;
